import { Link } from 'react-router-dom';
import i18n from 'infrastructure/i18n';
import { Routes } from 'router/routes';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats, stateToAbbreviation } from 'infrastructure/enums';
import {
  acceptanceStatusLabels,
  engagementAttemptsLabels,
} from 'infrastructure/hooks/patient-engagement-report';

import s from './styles.module.scss';

import type { IBaseTableColumns } from 'components/atoms/base-table/types';
import type { IPatientEngagementReportRow } from 'infrastructure/hooks/patient-engagement-report';

const formatDate = (date: string | null) =>
  date ? DateFormatter({ date, format: DateFormats['MM-dd-yyyy'] }) : '-';

export const columns: IBaseTableColumns<IPatientEngagementReportRow> = [
  {
    label: i18n.t('labels.patientName'),
    key: 'patientName',
    width: '190px',
    render: (name: string, record) => (
      <Link
        to={Routes.patientGuid(record.patientGuid)}
        className={s.patientLink}
      >
        {name}
      </Link>
    ),
  },
  {
    label: i18n.t('labels.mrn'),
    key: 'mrn',
    width: '110px',
    align: 'center',
    render: (mrn) => mrn || '-',
  },
  {
    label: i18n.t('labels.dob'),
    key: 'dob',
    width: '100px',
    render: (dob: string) => formatDate(dob),
  },
  {
    label: i18n.t('labels.patientState'),
    key: 'state',
    width: '90px',
    align: 'center',
    render: (state: IPatientEngagementReportRow['state']) =>
      stateToAbbreviation(state) || '-',
  },
  {
    label: i18n.t('labels.clinicName'),
    key: 'clinicName',
    width: '180px',
  },
  {
    label: i18n.t('patientEngagementReport.patientReferralDate'),
    key: 'referralReceivedDate',
    width: '120px',
    render: (date: string | null) => formatDate(date),
  },
  {
    label: i18n.t('patientEngagementReport.evelynCreateDate'),
    key: 'evelynCreateDate',
    width: '120px',
    render: (date: string) => formatDate(date),
  },
  {
    label: i18n.t('patientEngagementReport.engagementAttempts'),
    key: 'engagementAttempts',
    width: '150px',
    render: (value: IPatientEngagementReportRow['engagementAttempts']) =>
      value ? (
        <span className={s.badge}>
          {engagementAttemptsLabels[value] ?? '-'}
        </span>
      ) : (
        '-'
      ),
  },
  {
    label: i18n.t('patientEngagementReport.acceptanceStatus'),
    key: 'acceptanceStatus',
    width: '160px',
    render: (value: IPatientEngagementReportRow['acceptanceStatus']) =>
      value ? (
        <span className={s.badge}>{acceptanceStatusLabels[value] ?? '-'}</span>
      ) : (
        '-'
      ),
  },
  {
    label: i18n.t('patientEngagementReport.attemptDates'),
    key: 'attemptDates',
    width: '150px',
    render: (dates: string[]) =>
      dates.length === 0 ? (
        <span className={s.noAttempts}>
          {i18n.t('patientEngagementReport.noAttemptsYet')}
        </span>
      ) : (
        <div className={s.attemptDates}>
          {dates.map((date, i) => (
            <div key={`${date}-${i}`}>{formatDate(date)}</div>
          ))}
        </div>
      ),
  },
];
